import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {Clock, Star, Zap, Users, Award, Laptop, Bot, Video, Smartphone, AlertTriangle, Shield, ChevronDown, ChevronUp, Facebook, Instagram, Youtube, Linkedin, MessageCircleDashed as MessageCircle, DollarSign, X, Frown, BookOpen, Target, Trophy, CheckCircle, Volume2, RotateCcw, TrendingUp, Globe, Briefcase, BookOpenCheck, TrendingDown} from 'lucide-react';

const Vitalicio: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const [availableSpots, setAvailableSpots] = useState(37);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  useEffect(() => {
    // Google Tag Manager
    const gtmScript = document.createElement('script');
    gtmScript.async = true;
    gtmScript.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-PR5KTQF8';
    document.head.appendChild(gtmScript);

    const gtmDataLayer = document.createElement('script');
    gtmDataLayer.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'GTM-PR5KTQF8');
    `;
    document.head.appendChild(gtmDataLayer);

    // GTM noscript fallback
    const noscript = document.createElement('noscript');
    noscript.innerHTML = '<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-PR5KTQF8" height="0" width="0" style="display:none;visibility:hidden"></iframe>';
    document.body.insertBefore(noscript, document.body.firstChild);

    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + 14); // 14 days from now

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(distance % (1000 * 60 * 60 * 24) / (1000 * 60 * 60));
      const minutes = Math.floor(distance % (1000 * 60 * 60) / (1000 * 60));
      const seconds = Math.floor(distance % (1000 * 60) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });

      if (distance < 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => {
      clearInterval(timer);
      // Cleanup scripts
      const scripts = document.querySelectorAll('script[src*="converteai.net"], script[src*="googletagmanager.com"]');
      scripts.forEach((script) => script.remove());
    };
  }, []);

  // Simular redução de vagas
  useEffect(() => {
    const spotsTimer = setInterval(() => {
      setAvailableSpots((prev) => {
        if (prev > 36) {
          return prev - Math.floor(Math.random() * 3) - 1;
        }
        return prev;
      });
    }, 30000);

    return () => clearInterval(spotsTimer);
  }, []);

  const benefits = [
  { icon: Laptop, text: "EW Academy: mais de 400 horas de aulas" },
  { icon: Bot, text: "Lucy IA - Tutora virtual 24h, no seu whatsapp" },
  { icon: Video, text: "EW Live: Aulas ao vivo semanais" },
  { icon: Smartphone, text: "Apps iOS e Android para estudar onde quiser. Até offline" },
  { icon: Users, text: "Comunidade exclusiva com gamificação" },
  { icon: Award, text: "Certificado incluso de cada curso finalizado" }];

  const testimonials = [
  {
    name: "Marina Silva",
    photo: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop&crop=face&dpr=1&fm=webp",
    text: "Em 12 meses consegui minha primeira promoção internacional! O EW Pass mudou minha carreira completamente.",
    role: "Gerente de Marketing"
  },
  {
    name: "Carlos Mendes",
    photo: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop&crop=face&dpr=1&fm=webp",
    text: "Finalmente consegui assistir filmes sem legenda e me comunicar com confiança em reuniões internacionais.",
    role: "Engenheiro de Software"
  },
  {
    name: "Ana Paula",
    photo: "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop&crop=face&dpr=1&fm=webp",
    text: "A Lucy IA é incrível! Sempre disponível para tirar dúvidas. Nunca pensei que aprender inglês pudesse ser tão fácil.",
    role: "Estudante de Medicina"
  },
  {
    name: "Roberto Santos",
    photo: "https://images.pexels.com/photos/2182970/pexels-photo-2182970.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop&crop=face&dpr=1&fm=webp",
    text: "Consegui uma vaga nos EUA depois de 1 ano estudando. O método funciona mesmo, não é só marketing!",
    role: "Analista Financeiro"
  }];

  const problems = [
  {
    icon: DollarSign,
    title: "Cursos Caros",
    description: "Escolas tradicionais cobram milhares de reais por ano"
  },
  {
    icon: Clock,
    title: "Falta de Tempo",
    description: "Horários fixos que não se adaptam à sua rotina"
  },
  {
    icon: X,
    title: "Métodos Ultrapassados",
    description: "Livros e aulas monótonas que não funcionam"
  },
  {
    icon: Frown,
    title: "Alta Desistência",
    description: "90% dos alunos desistem nos primeiros 6 meses"
  }];

  const journeySteps = [
  {
    icon: BookOpen,
    title: "Método GO 4D",
    description: "Desde a primeira aula você já começa a prática da compreensão e conversação do inglês, com tutora virtual, aulas ao vivo semanais e mais de 400 horas de conteúdo exclusivo",
    gradient: "from-cyan-500 via-cyan-600 to-purple-600",
    number: "01"
  },
  {
    icon: Target,
    title: "Prática constante com a Lucy IA",
    description: "Direto no seu Whatsapp, 24 horas por dia, a tutora Lucy IA está ao seu lado te orientando e ajudando na prática, sem ter medo e vergonha de treinar o seu inglês",
    gradient: "from-purple-500 via-purple-600 to-fuchsia-600",
    number: "02"
  },
  {
    icon: Users,
    title: "EW Academy",
    description: "Não apenas uma IA, contamos com mais de 400 horas de aulas, do básico ao avançado, te mostrando o melhor caminho para sua jornada de aprendizagem.",
    gradient: "from-fuchsia-500 via-fuchsia-600 to-pink-600",
    number: "03"
  },
  {
    icon: Trophy,
    title: "Fluência Conquistada",
    description: "Você falando inglês com confiança em situações reais do dia a dia. Porque falar inglês não é só sobre aprender palavras, é sobre abrir portas, conquistar oportunidades e se conectar com o mundo.",
    gradient: "from-cyan-500 via-purple-500 to-fuchsia-500",
    number: "04"
  }];

  const guaranteeFeatures = [
  {
    icon: Clock,
    title: "14 Dias",
    description: "Tempo total para testar nossa plataforma sem riscos",
    color: "cyan"
  },
  {
    icon: CheckCircle,
    title: "100% do Dinheiro",
    description: "Devolução integral sem perguntas ou burocracias",
    color: "purple"
  },
  {
    icon: Shield,
    title: "Sem Riscos",
    description: "Você não tem nada a perder, apenas fluência a ganhar",
    color: "fuchsia"
  }];

  const guaranteeSteps = [
  {
    number: 1,
    title: "Assine a EW Pass Pro",
    description: "Faça sua assinatura e tenha acesso completo"
  },
  {
    number: 2,
    title: "Teste por 14 dias completos",
    description: "Explore todo o conteúdo e metodologia"
  },
  {
    number: 3,
    title: "Se não gostar, devolvemos 100%",
    description: "Sem perguntas, sem burocracias"
  }];

  const solutionFeatures = [
  {
    icon: Volume2,
    title: "Método GO 4D",
    description: "Nossa metodologia exclusiva que acelera seu aprendizado através de 4 dimensões: Ouvir, falar, ler e escrever. Treino, pronúncia e compreensão diárias usando a Lucy IA"
  },
  {
    icon: Clock,
    title: "15 minutos por dia",
    description: "Apenas 15 minutos diários são suficientes para você dominar o inglês de forma consistente e eficaz."
  },
  {
    icon: RotateCcw,
    title: "Foco na conversação",
    description: "Priorizamos a prática oral desde o primeiro dia, para que você ganhe confiança para falar inglês."
  },
  {
    icon: TrendingUp,
    title: "Progresso acelerado",
    description: "Veja resultados reais em semanas, não anos. Nossa metodologia é comprovadamente mais rápida."
  }];

  const englishBenefits = [
  {
    icon: Briefcase,
    title: "Mais oportunidades profissionais",
    description: "Dominar o inglês abre portas para novas oportunidades de trabalho no Brasil e no mundo, presencial ou home office.",
    image: "https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop&dpr=1&fm=webp"
  },
  {
    icon: BookOpenCheck,
    title: "Acesso a conteúdos em primeira mão",
    description: "Grande parte dos conteúdos é gerada na língua inglesa. Saia na frente dos seus concorrentes.",
    image: "https://images.pexels.com/photos/159711/books-bookstore-book-reading-159711.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop&dpr=1&fm=webp"
  },
  {
    icon: Globe,
    title: "Comunicação Global",
    description: "O mundo se abre para quem fala inglês. Em quase todos os países do globo você conseguirá conversar em inglês.",
    image: "https://images.pexels.com/photos/1595385/pexels-photo-1595385.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop&dpr=1&fm=webp"
  },
  {
    icon: TrendingUp,
    title: "Melhores Salários",
    description: "O profissional que fala inglês tem um salário em média 61% maior do que quem não fala o idioma e é a maior causa de progressão na carreira para 50% dos empregadores.",
    image: "https://images.pexels.com/photos/164527/pexels-photo-164527.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop&dpr=1&fm=webp"
  }];

  const faqData = [
  {
    question: "Como funciona o acesso vitalício?",
    answer: "Com o acesso vitalício, você paga uma única vez e tem acesso para sempre a todo o conteúdo do EW Pass, incluindo atualizações futuras. Não há mensalidades ou taxas adicionais."
  },
  {
    question: "Posso acessar o conteúdo offline?",
    answer: "Sim! Através dos nossos apps para iOS e Android, você pode baixar as aulas e estudar offline, onde e quando quiser, sem depender de conexão com a internet."
  },
  {
    question: "A Lucy IA realmente funciona 24 horas?",
    answer: "Sim! A Lucy é nossa tutora virtual que está disponível 24/7 para tirar suas dúvidas, corrigir exercícios e te ajudar em qualquer momento do seu aprendizado."
  },
  {
    question: "Qual é o nível do curso? É para iniciantes?",
    answer: "O EW Pass atende todos os níveis, do básico ao avançado. Temos conteúdo estruturado que se adapta ao seu nível atual e te leva até a fluência completa."
  },
  {
    question: "Como funciona a garantia de 14 dias?",
    answer: "Se por qualquer motivo você não ficar satisfeito com o EW Pass nos primeiros 14 dias, devolvemos 100% do seu dinheiro, sem perguntas ou burocracias."
  },
  {
    question: "Recebo certificado ao final do curso?",
    answer: "Sim! Você recebe um certificado de conclusão reconhecido que comprova suas habilidades em inglês, válido para usar no seu currículo e LinkedIn."
  },
  {
    question: "As aulas ao vivo são obrigatórias?",
    answer: "Não! As aulas ao vivo são um bônus extra. Todo o conteúdo principal está disponível gravado para você assistir no seu ritmo. As lives são para tirar dúvidas e praticar."
  },
  {
    question: "Posso parcelar o pagamento?",
    answer: "Sim! Você pode pagar à vista por R$ 297 ou parcelar em até 10x de R$ 33,44 no cartão de crédito, sem juros adicionais."
  }];

  // Função para rolar até a oferta
  const scrollToOffer = () => {
    const offerSection = document.getElementById('oferta-final');
    if (offerSection) {
      offerSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative py-6 px-4 z-20">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://static.lumi.new/26/26cb63c4aa4fe41e44624f57ce3867a5.webp"
              alt="EW PASS Logo"
              width="160"
              height="48"
              loading="eager"
              fetchpriority="high"
              decoding="async"
              className="h-10 md:h-12 w-auto" />
          </div>
          <div className="text-right">
            



          </div>
        </div>
      </motion.header>

      {/* Main Headline */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="pt-[48px] pl-[16px] pr-[16px] pb-[6px]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-3xl md:text-5xl font-bold text-fuchsia-400 mb-4">🔥 INVISTA EM VOCÊ HOJE 🔥</div>
          <h2 className="text-4xl md:text-6xl font-bold mb-8 leading-tight text-gray-900">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-600 to-cyan-600">Nunca mais gaste nenhum centavo</span> para aprender inglês
          </h2>
        </div>
      </motion.section>

      {/* Texto descritivo */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5 }}
        className="pt-[12px] pl-[16px] pr-[16px] pb-[32px]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="space-y-6 text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto">
            <p>
              Domine o inglês, conquiste fluência e multiplique suas oportunidades. 
              <span className="text-fuchsia-600 font-semibold"> ACESSO VITALÍCIO</span> a todo conteúdo do EW Pass.
            </p>
            <p>
              O maior desconto que já fizemos e <span className="text-cyan-600 font-semibold">não vai se repetir tão cedo! </span>
            </p>
            <p className="text-fuchsia-600 font-semibold">E essa é uma condição exclusiva!</p>
            <p>
              <span className="text-cyan-600 font-semibold">Compre UMA VEZ, estude para SEMPRE!</span>
            </p>
          </div>
        </div>
      </motion.section>

      {/* Urgência de Vagas Limitadas */}
      <motion.section
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.6 }}
        className="py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-gradient-to-r from-fuchsia-500 to-purple-600 rounded-2xl p-8 text-center shadow-xl">
            <div className="flex items-center justify-center gap-2 mb-6">
              <AlertTriangle className="w-8 h-8 text-cyan-400" />
              <span className="text-cyan-400 font-bold text-xl">VAGAS LIMITADAS</span>
            </div>
            
            <div className="mb-6">
              <div className="text-6xl md:text-7xl font-bold text-white mb-2">
                {availableSpots.toLocaleString()}
              </div>
              <div className="text-lg text-cyan-300 font-semibold mb-4">
                vagas restantes
              </div>
              <div className="text-gray-200 text-base">
                Apenas <span className="text-cyan-400 font-bold">1500 pessoas</span> terão acesso a essa promoção exclusiva
              </div>
            </div>

            {/* Barra de Progresso das Vagas */}
            <div className="bg-black/30 rounded-full h-4 mb-4 overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-fuchsia-400 to-cyan-500 rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: `${(1500 - availableSpots) / 1500 * 100}%` }}
                transition={{ duration: 1, ease: "easeOut" }} />
            </div>
            
            <div className="text-sm text-gray-200">
              <span className="text-cyan-400 font-semibold">{1500 - availableSpots}</span> pessoas já garantiram sua vaga
            </div>
          </div>
        </div>
      </motion.section>

      {/* Problems Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">
            Por que a maioria <span className="text-fuchsia-600">DESISTE</span> de aprender inglês?
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Identificamos os principais problemas que impedem brasileiros de alcançar a fluência
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-fuchsia-500">
                <div className="w-12 h-12 bg-gradient-to-r from-fuchsia-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                  <problem.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">{problem.title}</h3>
                <p className="text-gray-600">{problem.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Solution Features Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">
            A <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-600 to-cyan-600">SOLUÇÃO</span> que você procurava
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Método comprovado que já ajudou milhares de brasileiros a dominarem o inglês
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {solutionFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="bg-gradient-to-br from-fuchsia-50 to-cyan-50 rounded-xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-fuchsia-500">
                <div className="w-16 h-16 bg-gradient-to-r from-fuchsia-500 to-cyan-500 rounded-full flex items-center justify-center mb-4">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-gray-900">{feature.title}</h3>
                <p className="text-gray-700 text-lg leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Journey Steps Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">
            Sua jornada até a <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-600 to-cyan-600">FLUÊNCIA</span>
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Passo a passo estruturado para você dominar o inglês de forma natural
          </p>
          
          <div className="space-y-8">
            {journeySteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative">
                <div className="flex flex-col md:flex-row items-center gap-6 bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-fuchsia-500">
                  <div className={`flex-shrink-0 w-24 h-24 bg-gradient-to-br ${step.gradient} rounded-2xl flex items-center justify-center relative`}>
                    <step.icon className="w-12 h-12 text-white" />
                    <div className="absolute -top-3 -right-3 w-10 h-10 bg-cyan-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                      {step.number}
                    </div>
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-2xl font-bold mb-2 text-gray-900">{step.title}</h3>
                    <p className="text-gray-600 text-lg leading-relaxed">{step.description}</p>
                  </div>
                </div>
                
                {index < journeySteps.length - 1 && (
                  <div className="hidden md:block absolute left-12 top-full w-1 h-8 bg-gradient-to-b from-fuchsia-500 to-cyan-500 transform translate-y-0"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* English Benefits Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">
            Por que <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-600 to-cyan-600">DOMINAR INGLÊS</span> vai mudar sua vida?
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Descubra as vantagens reais de falar inglês fluentemente
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {englishBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-fuchsia-500">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={benefit.image}
                    alt={benefit.title}
                    width="400"
                    height="300"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-fuchsia-500 to-cyan-500 rounded-lg flex items-center justify-center">
                      <benefit.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-3 text-gray-900">{benefit.title}</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Benefits Grid */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Tudo que você recebe <span className="text-fuchsia-600">PARA SEMPRE</span>
          </h2>
          
          {/* Banner Principal - Logo após o título */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mb-12">
            <div className="text-center">
              <img
                src="https://static.lumi.new/9a/9a1d1f1975e8af201d3e4aea5f728987.webp"
                alt="Banner Principal EW Pass"
                width="1200"
                height="675"
                loading="lazy"
                decoding="async"
                className="w-full max-w-4xl mx-auto rounded-2xl shadow-2xl" />
            </div>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-fuchsia-500 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-fuchsia-500 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <p className="text-xl font-semibold text-gray-900">{benefit.text}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </motion.section>

      {/* Testimonials Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">
            Veja o que nossos <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-600 to-cyan-600">ALUNOS</span> dizem
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Histórias reais de pessoas que transformaram suas vidas com o EW Pass
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-fuchsia-500">
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={testimonial.photo}
                    alt={testimonial.name}
                    width="64"
                    height="64"
                    loading="lazy"
                    decoding="async"
                    className="w-16 h-16 rounded-full object-cover border-4 border-fuchsia-500" />
                  <div>
                    <h4 className="font-bold text-lg text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </div>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-fuchsia-500 text-fuchsia-500" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed italic">"{testimonial.text}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Guarantee Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-fuchsia-500 to-purple-600 rounded-full mb-6">
              <Shield className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-4 text-gray-900">
              Garantia <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-600 to-cyan-600">INCONDICIONAL</span> de 14 Dias
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Estamos tão confiantes que você vai amar o EW Pass que oferecemos uma garantia sem riscos
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {guaranteeFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center bg-gradient-to-br from-fuchsia-50 to-cyan-50 rounded-2xl p-8 border-2 border-transparent hover:border-fuchsia-500 transition-all duration-300">
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-${feature.color}-500 to-${feature.color}-600 rounded-full mb-4`}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600 text-lg">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl md:text-3xl font-bold text-center mb-8 text-black">Como Funciona?</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {guaranteeSteps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 h-full border-2 border-white/30">
                    <div className="w-12 h-12 bg-black text-white rounded-full flex items-center justify-center font-bold text-xl mb-4">
                      {step.number}
                    </div>
                    <h4 className="text-xl font-bold mb-2 text-black">{step.title}</h4>
                    <p className="text-gray-900">{step.description}</p>
                  </div>
                  {index < guaranteeSteps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                      <div className="text-white text-3xl">→</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <button
                onClick={scrollToOffer}
                className="bg-black text-white font-bold text-xl px-12 py-4 rounded-full hover:bg-gray-900 transition-all duration-300 transform hover:scale-105">
                QUERO TESTAR SEM RISCOS
              </button>
            </div>
          </div>
        </div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">
            Perguntas <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-600 to-cyan-600">FREQUENTES</span>
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            Tire todas as suas dúvidas sobre o EW Pass
          </p>
          
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl shadow-md overflow-hidden border-2 border-transparent hover:border-fuchsia-500 transition-all duration-300">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors">
                  <span className="font-semibold text-lg text-gray-900 pr-4">{faq.question}</span>
                  {openFAQ === index ? (
                    <ChevronUp className="w-6 h-6 text-fuchsia-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-fuchsia-600 flex-shrink-0" />
                  )}
                </button>
                {openFAQ === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 pb-5">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Final CTA */}
      <motion.section
        id="oferta-final"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="pt-[32px] pl-[16px] pr-[16px] pb-[32px]">
        <div className="max-w-2xl mx-auto text-center">
          <div className="relative bg-gradient-to-r from-fuchsia-400 to-cyan-500 rounded-2xl p-8 text-black">
            {/* Selo de 14 dias de garantia */}
            <div className="absolute -top-4 -right-4 z-10">
              <div className="bg-purple-500 text-white rounded-full w-24 h-24 flex flex-col items-center justify-center text-xs font-bold shadow-lg border-4 border-white transform rotate-12">
                <Shield className="w-4 h-4 mb-1" />
                <span>14 DIAS</span>
                <span>GARANTIA</span>
              </div>
            </div>

            <Zap className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              ACESSO VITALÍCIO
            </h3>
            
            {/* Preço riscado */}
            <div className="mb-4">
              <div className="text-lg line-through text-gray-700 mb-2">
                De R$ 397,00
              </div>
              <div className="text-lg font-semibold mb-2">
                Por apenas
              </div>
            </div>
            
            {/* Preços principais */}
            <div className="space-y-4 mb-6">
              <div className="text-3xl md:text-4xl font-bold">R$ 285,00 à vista <span className="text-lg">(no cartão ou pix)</span>

              </div>
              <div className="text-lg">
                ou <span className="text-2xl font-bold">10x R$ 32,09</span> no cartão de crédito
              </div>
            </div>
            
            <a
              href="https://checkout.ewpass.com.br/subscribe/ewpass-pro-vitalicio-26?&coupon=ewvitalicio"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white font-bold text-xl px-12 py-4 rounded-full hover:bg-gray-900 transition-all duration-300 transform hover:scale-105 w-full inline-block">
              GARANTIR ACESSO VITALÍCIO AGORA
            </a>
            
            <div className="flex items-center justify-center gap-2 mt-4 text-sm">
              <img
                src="https://static.lumi.new/a7/a7bda956ae1a1aa02b529366031aaf05.webp"
                alt="Compra Segura"
                loading="lazy"
                decoding="async"
                width="auto"
                height="44"
                className="h-11 w-auto rounded-lg" />

            </div>
            
            <p className="text-gray-700 text-sm mt-2">* Oferta válida apenas hoje!

            </p>
          </div>
          
          {/* Nova seção: Disponível para estudar offline - aproximada da oferta */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-6">
            <h4 className="text-lg md:text-xl font-bold text-gray-900 mb-4">Também disponível em dispositivos móveis</h4>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-4">
              <a
                href="https://play.google.com/store/apps/details?id=com.ewpass.mobile&hl=pt_BR"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105">
                <img
                  src="https://static.lumi.new/04/04a167a550be2df080da221af752c2d8.webp"
                  alt="Disponível no Google Play"
                  loading="lazy"
                  decoding="async"
                  width="auto"
                  height="48"
                  className="h-12 w-auto" />
              </a>
              <a
                href="https://apps.apple.com/br/app/ew-pass/id6752664261"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105">
                <img
                  src="https://static.lumi.new/41/4128788013032cb2940c52b89df93f82.webp"
                  alt="Disponível na App Store"
                  loading="lazy"
                  decoding="async"
                  width="auto"
                  height="48"
                  className="h-12 w-auto" />
              </a>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-300 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Logo e Descrição */}
            <div>
              <img
                src="https://static.lumi.new/26/26cb63c4aa4fe41e44624f57ce3867a5.webp"
                alt="EW PASS Logo"
                loading="lazy"
                decoding="async"
                width="160"
                height="40"
                className="h-10 mb-4" />
              <p className="text-gray-600 text-sm">
                Seu passaporte definitivo para aprender inglês e conquistar o mundo.
              </p>
            </div>

            {/* Links Rápidos */}
            <div>
              <h4 className="font-bold text-gray-900 mb-4">Links Rápidos</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="#" className="hover:text-fuchsia-600 transition-colors">Sobre Nós</a></li>
                <li><a href="#" className="hover:text-fuchsia-600 transition-colors">Termos de Uso</a></li>
                <li><a href="#" className="hover:text-fuchsia-600 transition-colors">Política de Privacidade</a></li>
                <li><a href="#" className="hover:text-fuchsia-600 transition-colors">Contato</a></li>
              </ul>
            </div>

            {/* Redes Sociais */}
            <div>
              <h4 className="font-bold text-gray-900 mb-4">Siga-nos</h4>
              <div className="flex gap-4">
                <a
                  href="https://facebook.com/ewpass"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gradient-to-r from-fuchsia-500 to-cyan-500 rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                  <Facebook className="w-5 h-5 text-white" />
                </a>
                <a
                  href="https://instagram.com/ewpass"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gradient-to-r from-fuchsia-500 to-cyan-500 rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                  <Instagram className="w-5 h-5 text-white" />
                </a>
                <a
                  href="https://youtube.com/ewpass"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gradient-to-r from-fuchsia-500 to-cyan-500 rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                  <Youtube className="w-5 h-5 text-white" />
                </a>
                <a
                  href="https://linkedin.com/company/ewpass"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gradient-to-r from-fuchsia-500 to-cyan-500 rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                  <Linkedin className="w-5 h-5 text-white" />
                </a>
              </div>
            </div>
          </div>
          
          {/* Texto de Copyright */}
          <div className="text-center text-gray-600 pt-8 border-t border-gray-300">
            <p>&copy; EW Pass - Seu passaporte definitivo para aprender inglês. </p>
            <p>EW Pass é um produto da English Work Sociedade de Ensino Ltda</p>
            <p> © 2025 - Todos os direitos reservados | CNPJ: 48.620.557/0001-51</p>
          </div>
        </div>
      </footer>

      {/* Botão Flutuante do WhatsApp */}
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="fixed bottom-6 right-6 z-50">
        <a
          href="https://wa.me/5527997139118?text=Olá! Gostaria de saber mais sobre a Black Friday da EW Pass."
          target="_blank"
          rel="noopener noreferrer"
          className="bg-purple-600 hover:bg-purple-700 text-white p-4 rounded-full shadow-xl transition-all duration-300 transform hover:scale-110 flex items-center gap-3 group">
          <MessageCircle className="w-6 h-6" />
          <span className="hidden group-hover:block whitespace-nowrap bg-gray-900 text-white px-3 py-1 rounded-lg absolute right-full mr-3 text-sm">
            Fale conosco!
          </span>
        </a>
      </motion.div>
    </div>);

};

export default Vitalicio;